import { Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FilterBar from "./FilterBar";
import { DashboardFilters, TabKey, parseFilters, serializeFilters, statsQuery } from "./filters";
import { Meta, TabProps } from "./types";
import { useStats } from "./useStats";
import OverviewTab from "./tabs/OverviewTab";
import LiveTab from "./tabs/LiveTab";
import AudienceTab from "./tabs/AudienceTab";
import ChatReactionsTab from "./tabs/ChatReactionsTab";
import GamesTab from "./tabs/GamesTab";
import GameTimelineDrawer from "./components/GameTimelineDrawer";
import WatchPartiesTab from "./tabs/WatchPartiesTab";
import MomentsTab from "./tabs/MomentsTab";
import SecondScreenTab from "./tabs/SecondScreenTab";
import SafetyTab from "./tabs/SafetyTab";
import TenantsTab from "./tabs/TenantsTab";

const TAB_LABELS: Array<[TabKey, string]> = [
  ["overview", "Overview"], ["live", "Live"], ["audience", "Audience"], ["chat", "Chat & Reactions"],
  ["games", "Games"], ["watch-parties", "Watch Parties"], ["moments", "Moments"],
  ["second-screen", "Second Screen"], ["safety", "Safety"], ["tenants", "Tenants"],
];

const AudienceLayer: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams.toString()), [searchParams]);
  const [reloadKey, setReloadKey] = useState(0);
  const [openGame, setOpenGame] = useState<{ tenant: string; gameId: string } | null>(null);
  // Pinned, not recomputed per render: "last 24 hours" would otherwise change
  // the query on every render and refetch forever.
  const [now, setNow] = useState(() => new Date());
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const meta = useStats<Meta>("/meta", "", 0);

  const update = (next: Partial<DashboardFilters>) => {
    if ("preset" in next || "from" in next || "to" in next) setNow(new Date());
    setSearchParams(serializeFilters({ ...filters, ...next }));
  };
  const refresh = () => {
    setNow(new Date());
    setReloadKey((k) => k + 1);
  };
  const openTimeline = (tenant: string, gameId: string) => setOpenGame({ tenant, gameId });

  if (meta.error?.kind === "forbidden") {
    return (
      <Paper variant="outlined" sx={{ p: 4 }}>
        <Typography variant="h6">Not authorized</Typography>
        <Typography color="text.secondary">{meta.error.message}</Typography>
      </Paper>
    );
  }

  const query = statsQuery(filters, now, tz);
  const liveQuery = statsQuery(filters, now, tz, { range: false });
  const props: TabProps = { query, reloadKey, gameId: filters.gameId };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Audience Layer</Typography>
      <FilterBar
        filters={filters}
        tenants={meta.envelope?.data.tenants ?? []}
        maxRangeDays={meta.envelope?.data.maxRangeDays ?? 90}
        onChange={update}
        onRefresh={refresh}
      />
      <Tabs value={filters.tab} onChange={(_, tab: TabKey) => update({ tab })} variant="scrollable" sx={{ mb: 2 }}>
        {TAB_LABELS.map(([key, label]) => <Tab key={key} value={key} label={label} />)}
      </Tabs>
      <Box>
        {filters.tab === "overview" && <OverviewTab {...props} />}
        {filters.tab === "live" && <LiveTab query={liveQuery} reloadKey={reloadKey} />}
        {filters.tab === "audience" && <AudienceTab {...props} />}
        {filters.tab === "chat" && <ChatReactionsTab {...props} onOpenGame={openTimeline} />}
        {filters.tab === "games" && <GamesTab {...props} onOpenGame={openTimeline} />}
        {filters.tab === "watch-parties" && <WatchPartiesTab {...props} />}
        {filters.tab === "moments" && <MomentsTab {...props} />}
        {filters.tab === "second-screen" && <SecondScreenTab {...props} />}
        {filters.tab === "safety" && <SafetyTab {...props} />}
        {filters.tab === "tenants" && <TenantsTab {...props} />}
      </Box>
      <GameTimelineDrawer game={openGame} reloadKey={reloadKey} onClose={() => setOpenGame(null)} />
    </Box>
  );
};

export default AudienceLayer;
